import { DEFAULT, FORECAST, STANDARD, VIEW_SHARE_PRINCIPAL_TYPE, VIEW_SHARE_ROLE } from '../types/ViewTypes';
import { transformBackendViewToInternalFormat } from './backendViews';
import { VIEWS_NAMESPACE_NONE } from '../constants';
const narrowViewType = type => {
  if (type === STANDARD || type === FORECAST || type === DEFAULT) {
    return type;
  }
  return undefined;
};

// Narrows the CHIRP ViewRole (which includes the unhandledEnumCase symbol) down
// to a concrete View share role, or undefined when it is absent/unrecognized.
const narrowViewRole = role => role === VIEW_SHARE_ROLE.OWNER || role === VIEW_SHARE_ROLE.COLLABORATOR || role === VIEW_SHARE_ROLE.MEMBER ? role : undefined;
const getSharedUserIdsFromPermissions = permissions => permissions.filter(p => p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.USER && p.viewRole !== VIEW_SHARE_ROLE.OWNER).map(p => p.principalId);
const getSharedTeamIdsFromPermissions = permissions => permissions.filter(p => p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.TEAM && p.viewRole !== VIEW_SHARE_ROLE.OWNER).map(p => p.principalId);

// Carries the explicit per-principal role (OWNER / COLLABORATOR / MEMBER) for
// each USER/TEAM straight off the CHIRP response so the Shared with column can
// render real roles instead of the legacy owner/member shim.
const toViewSharePermissions = permissions => permissions.filter(p => p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.USER || p.principalType === VIEW_SHARE_PRINCIPAL_TYPE.TEAM).map(p => {
  var _narrowViewRole;
  return {
    principalId: p.principalId,
    principalType: p.principalType,
    role: (_narrowViewRole = narrowViewRole(p.viewRole)) !== null && _narrowViewRole !== void 0 ? _narrowViewRole : VIEW_SHARE_ROLE.MEMBER
  };
});

/**
 * Maps a CHIRP getViewsByComposableSearch result (a SalesView plus its inline
 * permissions list) into the internal View shape. The JSON-config fields
 * (columns/state/filters/etc.) arrive in the same stringified shape as the v4
 * BackendView, so we reuse the existing v4 transform for those. The inline
 * permissions become `view.permissions` (the source of truth that the Owner and
 * Shared with columns render from), and the current user's role becomes
 * `view.userRole`, which drives the edit/delete permission check (the backend
 * returns OWNER for view owners and super-admins). ownerId/teamId/
 * permissionDetails are kept in their v4 shape only for the update mutation
 * payload, the legacy v4 path, and the legacy shared-with summary.
 */
export const transformViewWithPermissionsToInternalFormat = ({
  view,
  permissions = [],
  userRole
}) => {
  var _view$name, _view$objectTypeId, _view$private, _view$namespaceName, _view$columns, _view$state, _view$filterGroups, _view$quickFilters;
  const backendViewLike = {
    id: String(view.id),
    idLabel: view.idLabel,
    name: (_view$name = view.name) !== null && _view$name !== void 0 ? _view$name : '',
    objectTypeId: (_view$objectTypeId = view.objectTypeId) !== null && _view$objectTypeId !== void 0 ? _view$objectTypeId : '',
    ownerId: view.ownerId,
    private: (_view$private = view.private) !== null && _view$private !== void 0 ? _view$private : false,
    namespaceName: (_view$namespaceName = view.namespaceName) !== null && _view$namespaceName !== void 0 ? _view$namespaceName : VIEWS_NAMESPACE_NONE,
    type: narrowViewType(view.type),
    columns: (_view$columns = view.columns) !== null && _view$columns !== void 0 ? _view$columns : '',
    state: (_view$state = view.state) !== null && _view$state !== void 0 ? _view$state : '',
    filterGroups: (_view$filterGroups = view.filterGroups) !== null && _view$filterGroups !== void 0 ? _view$filterGroups : '',
    quickFilters: (_view$quickFilters = view.quickFilters) !== null && _view$quickFilters !== void 0 ? _view$quickFilters : '',
    quickFilterProperties: view.quickFilterProperties,
    currentViewVisualization: view.currentViewVisualization,
    viewColor: view.viewColor
  };
  const baseView = transformBackendViewToInternalFormat(backendViewLike);
  const sharedUserIds = getSharedUserIdsFromPermissions(permissions);
  const sharedTeamIds = getSharedTeamIdsFromPermissions(permissions);
  const viewPermissions = toViewSharePermissions(permissions);
  return Object.assign({}, baseView, {
    // v4-shaped fields the update mutation payload / legacy v4 path / legacy
    // shared-with summary still read. ownerId is the view's primary owner;
    // teamId/permissionDetails reflect the non-owner shares.
    ownerId: view.ownerId,
    teamId: sharedTeamIds.length > 0 ? sharedTeamIds[0] : baseView.teamId,
    permissionDetails: {
      userIds: sharedUserIds
    },
    // Source of truth for rendering the Owner and Shared with columns: the full
    // per-principal role list straight off the composable-search response.
    permissions: viewPermissions.length > 0 ? viewPermissions : undefined,
    // The current user's role on this view drives the edit/delete check.
    userRole: narrowViewRole(userRole)
  });
};